<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5>Catatan Kas Harian</h5>
            </template>

            <div class="big-filter-label filter-form-wrapper mb-3">
                <b-row class="align-items-end">
                    <b-col md="6">
                        <b-form-group label="Tanggal">
                            <b-form-input
                                v-model="filterDate"
                                type="date"
                                name="filterDate"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="align-items-center">
                    <b-col cols="12">
                        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div class="d-flex gap-2 flex-wrap">
                            <b-button
                                variant="pgiBtn"
                                :disabled="isBusy"
                                name="catatanKasSearchBtn"
                                @click="onSearch"
                            >
                                Search
                            </b-button>
                            <b-button
                                variant="pgiBtn"
                                :disabled="isBusy"
                                name="catatanKasResetBtn"
                                @click="onReset"
                            >
                                Reset
                            </b-button>
                            </div>
                            <div class="d-flex gap-2 flex-wrap">
                                <b-button
                                    variant="pgiBtn"
                                    :disabled="isLoadingDownload"
                                    name="catatanKasDownloadBtn"
                                    @click="confirmDownloadCatatanKas"
                                >
                                    <i class="fa fa-print mr-2"></i>
                                    {{ isLoadingDownload ? "Proses..." : "Cetak Catatan Kas" }}
                                </b-button>
                                <b-button
                                    v-if="hasPermissionInput"
                                    variant="pgiBtn"
                                    name="catatanKasInputBtn"
                                    @click="goToInput"
                                >
                                    Input Catatan Kas
                                </b-button>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>

            <b-table
                bordered
                hover
                show-empty
                :busy="isBusy"
                :items="items"
                :fields="fields"
                responsive
                class="mb-3"
            >
                <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading data...</strong>
                    </div>
                </template>
                <template v-slot:empty>
                    Belum ada data catatan kas.
                </template>
                <template v-slot:cell(saldoKasProgram)="row">
                    <div class="text-right font-weight-bold">Rp. {{ $helper.addThousandSeparator(row.item.saldoKasProgramOperasional) }}</div>
                    <div class="text-right text-muted small">
                        Laci: Rp. {{ $helper.addThousandSeparator(row.item.saldoProgramLaci) }}
                    </div>
                    <div class="text-right text-muted small">
                        Brankas: Rp. {{ $helper.addThousandSeparator(row.item.saldoProgramBrankas) }}
                    </div>
                    <div class="text-right text-muted small border-top mt-1 pt-1">
                        Kas Kecil: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasKecil) }}
                    </div>
                </template>
                <template v-slot:cell(saldoKasFisik)="row">
                    <div class="text-right font-weight-bold">Rp. {{ $helper.addThousandSeparator(row.item.saldoKasFisikOperasional) }}</div>
                    <div class="text-right text-muted small">
                        Laci: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasFisikLaci) }}
                    </div>
                    <div class="text-right text-muted small">
                        Brankas: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasFisikBrankas) }}
                    </div>
                    <div class="text-right text-muted small border-top mt-1 pt-1">
                        Kas Kecil: Rp. {{ $helper.addThousandSeparator(row.item.totalKasKecil) }}
                    </div>
                    <div class="text-right text-muted small mt-1">
                        Materai Tersedia: {{ row.item.totalKasMateraiQty }}
                    </div>
                    <div class="text-right text-muted small">
                        Materai Terjual: {{ row.item.totalKasMateraiTerjualQty }}
                    </div>
                </template>
                <template v-slot:cell(selisih)="row">
                    <div class="text-right font-weight-bold" :class="getSelisihClass(row.item.selisihOperasional)">
                        Rp. {{ $helper.addThousandSeparator(getAbsoluteSelisih(row.item.selisihOperasional)) }}
                    </div>
                    <div class="text-right small" :class="getSelisihClass(row.item.selisihLaci)">
                        Laci: Rp. {{ $helper.addThousandSeparator(getAbsoluteSelisih(row.item.selisihLaci)) }}
                    </div>
                    <div class="text-right small" :class="getSelisihClass(row.item.selisihBrankas)">
                        Brankas: Rp. {{ $helper.addThousandSeparator(getAbsoluteSelisih(row.item.selisihBrankas)) }}
                    </div>
                    <div class="text-right small border-top mt-1 pt-1" :class="getSelisihClass(row.item.selisihKasKecil)">
                        Kas Kecil: Rp. {{ $helper.addThousandSeparator(getAbsoluteSelisih(row.item.selisihKasKecil)) }}
                    </div>
                </template>
                <template v-slot:cell(informasiPetugas)="row">
                    <div class="smallText">
                        <strong>Dibuat Pada:</strong> {{ formatDateTime(row.item.createdAt || row.item.tanggal) }}
                    </div>
                    <div class="smallText">
                        <strong>Dibuat Oleh:</strong> {{ row.item.createdBy || "-" }}
                    </div>
                    <div class="smallText">
                        <strong>Staff Opname:</strong> {{ row.item.staffOpname || "-" }}
                    </div>
                </template>
                <template v-slot:cell(informasiVerifikasi)="row">
                    <div class="smallText mb-1">
                        <strong>Status:</strong>
                        <span class="ml-1" :class="getStatusClass(row.item.status)">
                            {{ getStatusLabel(row.item.status) }}
                        </span>
                    </div>
                    <div class="smallText">
                        <strong>Diverifikasi Staff Opname Pada:</strong> {{ row.item.approvedStaffOpnameAt || "-" }}
                    </div>
                    <template v-if="isCancelledStatus(row.item.status)">
                        <div class="smallText">
                            <strong>Dibatalkan Oleh:</strong> {{ row.item.cancelledBy || "-" }}
                        </div>
                        <div class="smallText">
                            <strong>Dibatalkan Pada:</strong> {{ row.item.cancelledAt || "-" }}
                        </div>
                        <div class="smallText">
                            <strong>Alasan Dibatalkan:</strong> {{ row.item.rejectionReason || "-" }}
                        </div>
                    </template>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="d-flex flex-wrap gap-2">
                        <b-button
                            size="sm"
                            variant="primary"
                            name="catatanKasDetailBtn"
                            v-b-tooltip.hover
                            title="Detail"
                            @click="openDetail(row.item)"
                        >
                            <i class="fa fa-info"></i>
                        </b-button>
                    </div>
                </template>
            </b-table>

            <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                    <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                        <template v-slot:first>
                            <b-form-select-option :value="null" disabled />
                        </template>
                    </b-form-select>
                    <span class="ml-2">{{ totalViewText }}</span>
                </b-input-group>
                <b-pagination class="mb-0"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="catatan-kas-table"
                    @input="onPageChange"
                ></b-pagination>
            </div>
        </b-card>

        <ModalConfirm
            id="modal-confirm-download-catatan-kas"
            title="Konfirmasi Cetak Catatan Kas"
            :isSubmitting="isLoadingDownload"
            desc="Apakah Anda yakin ingin cetak catatan kas dalam format PDF?"
            @submit="downloadCatatanKas"
        />

        <b-modal id="modal-download-catatan-kas" centered title="Cetak Catatan Kas" hide-footer no-close-on-backdrop size="xl">
            <iframe
                v-if="filePathDownload"
                :src="$baseFilePath + filePathDownload"
                style="width: 100%; height: 75vh; border: 0;"
            />
            <p v-else class="mb-0">File tidak tersedia.</p>
        </b-modal>
    </div>
</template>

<script>
import CONSTANTS from '@/constants'

export default {
    name: "catatan-kas-list",
    data() {
        return {
            initStatus: 0,
            isBusy: false,
            filterDate: null,
            fields: [
                { key: "shift", label: "Shift" },
                { key: "saldoKasProgram", label: "Saldo Kas Sistem", thClass: 'min-w-160', tdClass: 'min-w-160' },
                { key: "saldoKasFisik", label: "Saldo Kas Fisik", thClass: 'min-w-160', tdClass: 'min-w-160' },
                { key: "selisih", label: "Selisih", thClass: 'min-w-160', tdClass: 'min-w-160' },
                { key: "informasiPetugas", label: "Informasi Petugas" },
                { key: "informasiVerifikasi", label: "Informasi Verifikasi" },
                { key: "action", label: "Aksi", thClass: 'min-w-100', tdClass: 'min-w-100' },
            ],
            items: [],
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalViewText: "",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
            isLoadingDownload: false,
            filePathDownload: "",
        };
    },
    computed: {
        hasPermissionInput() {
            return this.$helper.hasPermissionMenuChildren('KKL');
        },
    },
    methods: {
        async init() {
            this.initStatus = 0
            try {
                this.filterDate = this.getTodayDateValue();
                await this.getItems()
                this.initStatus = 1
            } catch (error) {
                this.$helper.parseError(this, error)
                this.initStatus = -1
            }
        },
        getTodayDateValue() {
            const date = new Date();
            const month = `${date.getMonth() + 1}`.padStart(2, "0");
            const day = `${date.getDate()}`.padStart(2, "0");
            return `${date.getFullYear()}-${month}-${day}`;
        },
        getShiftLabel(shift) {
            if (Number(shift) === 3) return "Malam";
            if (Number(shift) === 2) return "Siang";
            return "Pagi";
        },
        getSelisihClass(selisih) {
            if (Number(selisih) < 0) return "text-success";
            if (Number(selisih) > 0) return "text-danger";
            return "";
        },
        getAbsoluteSelisih(value) {
            return Math.abs(Number(value || 0));
        },
        getMateraiQty(value, kelipatanMaterai) {
            const nominal = Number(kelipatanMaterai || 0);
            if (nominal <= 0) return 0;
            return Number(value || 0) / nominal;
        },
        formatDateTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-";
            if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
            return `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`;
        },
        getStatusLabel(status) {
            if (Number(status) === 2) return "Dibatalkan";
            if (Number(status) === 3) return "Diverifikasi";
            return "Pending";
        },
        getStatusClass(status) {
            if (Number(status) === 2) return "status-box-md status-box-danger";
            if (Number(status) === 3) return "status-box-md status-box-success";
            return "status-box-md status-box-warning";
        },
        buildQueryParams() {
            return {
                cabangId: this.$helper.getCabangId(),
                tanggal: this.filterDate || this.getTodayDateValue(),
                page: this.currentPage - 1,
                size: this.perPage,
            };
        },
        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];
            this.currentPage = isNaN(page) ? 1 : page;

            const params = this.buildQueryParams();

            return this.$axios.get("log-book-kasir/", { params })
                .then((response) => {
                    const result = response.data || {};
                    const content = Array.isArray(result.content) ? result.content : [];

                    this.items = content.map((item) => ({
                        id: item.id,
                        tanggal: item.tanggal,
                        createdAt: item.createdAt || item.tanggal,
                        shift: this.getShiftLabel(item.shift),
                        saldoKasProgram: Number(item.saldoProgram || 0),
                        saldoProgramLaci: Number(item.saldoLaci || 0),
                        saldoProgramBrankas: Number(item.saldoBrankas || 0),
                        saldoKasKecil: Number(item.saldoKasKecil || 0),
                        saldoKasFisik: Number(item.totalDetails || 0),
                        saldoKasFisikLaci: Number(item.totalKasLaci || 0),
                        saldoKasFisikBrankas: Number(item.totalKasBrankas || 0),
                        totalKasKecil: Number(item.totalKasKecil || 0),
                        saldoKasProgramOperasional: Number(item.saldoLaci || 0) + Number(item.saldoBrankas || 0),
                        saldoKasFisikOperasional: Number(item.totalKasLaci || 0) + Number(item.totalKasBrankas || 0),
                        totalKasMateraiQty: this.getMateraiQty(
                            item.totalKasMaterai,
                            item.kelipatanMaterai
                        ),
                        totalKasMateraiTerjualQty: this.getMateraiQty(
                            item.totalKasMateraiTerjual,
                            item.kelipatanMaterai
                        ),
                        selisih: Number(item.selisih || 0),
                        selisihLaci: Number(item.selisihLaci || 0),
                        selisihBrankas: Number(item.selisihBrankas || 0),
                        selisihKasKecil: Number(item.selisihKasKecil || 0),
                        selisihOperasional: Number(item.selisihLaci || 0) + Number(item.selisihBrankas || 0),
                        createdBy: item.createdBy || "-",
                        staffOpname: item.saksi || "-",
                        approvedStaffOpnameAt: this.formatDateTime(item.staffProcessedAt),
                        cancelledBy: item.rejectedBy || "",
                        cancelledAt: this.formatDateTime(item.rejectedAt),
                        status: item.status || 1,
                        rejectionReason: item.rejectedNote || "",
                    }));

                    this.totalRows = Number(result.totalElements || 0);

                    if (this.totalRows === 0) {
                        this.totalViewText = "0 dari 0 data";
                        return;
                    }

                    const pg = this.currentPage - 1;
                    const prev = pg * this.show + 1;
                    let limit = pg * this.show + this.show;
                    if (limit > this.totalRows) limit = this.totalRows;
                    this.totalViewText = `Menampilkan ${prev} - ${limit} dari ${this.totalRows} entri`;
                })
                .catch((error) => {
                    throw error;
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        onSearch() {
            if (this.$helper.isNullOrEmpty(this.filterDate)) {
                this.$helper.toastErr(this, "Tanggal wajib diisi.");
                return;
            }
            this.getItems(1);
        },
        onReset() {
            this.filterDate = this.getTodayDateValue();
            this.getItems(1);
        },
        onLimitChange(newPerPage) {
            this.show = newPerPage;
            this.perPage = newPerPage;
            this.getItems(1);
        },
        onPageChange(newPage) {
            this.getItems(newPage);
        },
        goToInput() {
            this.$router.push({ path: `${this.$route.path}/input` });
        },
        confirmDownloadCatatanKas() {
            if (this.$helper.isNullOrEmpty(this.filterDate)) {
                this.$helper.toastErr(this, "Tanggal wajib diisi.");
                return;
            }

            this.filePathDownload = "";
            this.$bvModal.show("modal-confirm-download-catatan-kas");
        },
        downloadCatatanKas() {
            this.isLoadingDownload = true;

            const params = {
                cabangId: this.$helper.getCabangId(),
                tanggal: this.filterDate,
            };

            this.$axios.get("log-book-kasir/export-pdf", { params })
                .then((response) => {
                    const fileName = response.data && response.data.name ? response.data.name : "";
                    if (this.$helper.isNullOrEmpty(fileName)) {
                        this.$helper.toastErr(this, "File download tidak ditemukan.");
                        return;
                    }
                    this.filePathDownload = fileName;
                    this.$bvModal.hide("modal-confirm-download-catatan-kas");
                    this.$bvModal.show("modal-download-catatan-kas");
                })
                .catch((error) => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isLoadingDownload = false;
                });
        },
        openDetail(item) {
            this.$router.push({ path: `${this.$route.path}/${item.id}` });
        },
        isCancelledStatus(status) {
            return Number(status) === 2;
        },
    },
    created() {
        this.init();
    },
};
</script>
